import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import QuickStats from './Dashboard elements/Quick Stats/QuickStats';
import TodaysTasks from './Dashboard elements/Today\'s Tasks/TodaysTasks';
import TeamActivitiesWidget from './Dashboard elements/Team Activities/TeamActivitiesWidget';
import TeamActivitiesModal from './Dashboard elements/Team Activities/TeamActivitiesModal';
import InventoryWidget from './Dashboard elements/Inventory/InventoryWidget';
import InventoryModal from './Inventory/InventoryModal';
import ProfileCard from './Dashboard elements/Your profile/ProfileCard';
import ChallengesBox from './Dashboard elements/Challenges/ChallengesBox';
import FeedbackModal from './Feedback/FeedbackModal';
import ProfileSettingsModal from './Profile/ProfileSettingsModal';
import InviteModal from './Invite/InviteModal';
import MasterTaskLogo from '../assets/MasterTask-logo.svg';
import AddUserIcon from '../assets/add-user.svg';

function Dashboard({ user, onLogout, onNavigate, isAdmin }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_contacts: 0,
    added_this_week: 0,
    tasks_completed_today: 0,
    pending_tasks: 0
  });
  const [loading, setLoading] = useState(true);
  const [showTeamActivitiesModal, setShowTeamActivitiesModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const statsResponse = await fetch('/backend/api/dashboard/stats.php', {
        credentials: 'include'
      });

      if (!statsResponse.ok) {
        throw new Error(`HTTP ${statsResponse.status}: ${statsResponse.statusText}`);
      }

      const statsData = await statsResponse.json();

      if (statsData.success) {
        setStats(statsData.stats);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskComplete = () => {
    // Refresh stats when a task is completed
    fetchDashboardData();
  };

  const handlePrefsUpdate = (newPrefs) => {
    // Refresh dashboard to reload stats with new preferences
    fetchDashboardData();
  };

  const handleProfileUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-logo">
          <img src={MasterTaskLogo} alt="MasterTask" />
        </div>
        <img 
          src={AddUserIcon} 
          alt="Invite" 
          className="invite-icon"
          onClick={() => setShowInviteModal(true)}
        />
        <div className="dashboard-actions">
          <button 
            className="nav-button invite-button"
            onClick={() => setShowInviteModal(true)}
          >
            Invite
          </button>
          <button
            className="nav-button calendar-button"
            onClick={() => navigate('/calendar')}
          >
            Calendar
          </button>
          <button 
            className="nav-button feedback-button"
            onClick={() => setShowFeedbackModal(true)}
          >
            Feedback
          </button>
          <button 
            className="nav-button"
            onClick={() => onNavigate('contacts')}
          >
            My Contacts
          </button>
          <button 
            className="nav-button"
            onClick={() => onNavigate('network')}
          >
            My Network
          </button>
          {isAdmin && (
            <button 
              className="nav-button admin-button"
              onClick={() => onNavigate('admin')}
            >
              Admin Panel
            </button>
          )}
          <button 
            className="nav-button logout-button"
            onClick={onLogout}
          >
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        <ProfileCard 
          user={currentUser} 
          onOpenSettings={() => setShowProfileSettings(true)}
        />
        <QuickStats stats={stats} user={currentUser} onPrefsUpdate={handlePrefsUpdate} />
        <TodaysTasks onTaskComplete={handleTaskComplete} onViewAll={() => onNavigate('tasks')} />
        <ChallengesBox onViewAll={(view, id) => onNavigate(view || 'challenges', id)} />
        <InventoryWidget onViewAll={() => setShowInventoryModal(true)} />
        <TeamActivitiesWidget onViewAll={() => setShowTeamActivitiesModal(true)} />
      </div>

      {showTeamActivitiesModal && (
        <TeamActivitiesModal 
          isOpen={showTeamActivitiesModal}
          onClose={() => setShowTeamActivitiesModal(false)}
          user={user}
        />
      )}

      {showInventoryModal && (
        <InventoryModal 
          isOpen={showInventoryModal}
          onClose={() => setShowInventoryModal(false)}
          user={user}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal 
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          user={currentUser}
          isAdmin={isAdmin}
        />
      )}

      {showProfileSettings && (
        <ProfileSettingsModal 
          isOpen={showProfileSettings}
          onClose={() => setShowProfileSettings(false)}
          user={currentUser}
          onUpdate={handleProfileUpdate}
        />
      )}

      {showInviteModal && (
        <InviteModal 
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          user={currentUser}
        />
      )}

      {/* Getting Started card temporarily hidden */}
      {/* <div className="getting-started">
        <h3 className="getting-started-title">Getting Started</h3>
        <p className="getting-started-text">Welcome to MasterTask! Here's what you can do next:</p>
        <ul className="getting-started-list">
          <li>Add your first 25 contacts to activate your dashboard</li>
          <li>Set up your network hierarchy</li>
          <li>Start tracking your daily tasks</li>
        </ul>
      </div> */}
    </div>
  );
}

export default Dashboard;